import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  useTheme
} from '@mui/material';
import {
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  Image as ImageIcon,
  PhotoLibrary as PhotoLibraryIcon,
  CompareArrows as CompareIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon
} from '@mui/icons-material';
import { ABCDEResult } from '../../../../services/abcdeAPI';

interface AssessmentHistoryProps {
  existingResults: ABCDEResult[];
  currentAssessmentId: string | null;
  isUpdating: boolean;
  onEditAssessment: (result: ABCDEResult) => void;
  onNavigateToAssessment: (annotationId: string) => void;
  onCompareAssessments: (current: ABCDEResult, historical: any) => void;
}

interface TemporalAssessment {
  id: string;
  annotationId: string;
  assessment: any;
  imageUrl?: string;
  createdAt: string;
  isCurrent: boolean;
  temporalIndex: number;
  isBaseline: boolean;
  sessionType: string;
  annotationDetails?: any;
  previousAssessment?: TemporalAssessment | null;
  nextAssessment?: TemporalAssessment | null;
  isLatest: boolean;
}

const AssessmentHistory: React.FC<AssessmentHistoryProps> = ({
  existingResults,
  currentAssessmentId,
  isUpdating,
  onEditAssessment,
  onNavigateToAssessment,
  onCompareAssessments
}) => {
  const theme = useTheme();

  const getRiskLevel = (score: number) => {
    if (score <= 2) return { 
      level: 'Low Risk', 
      color: theme.palette.success.main, 
      bg: alpha(theme.palette.success.main, 0.1),
      severity: 'success' as const
    };
    if (score <= 4) return { 
      level: 'Moderate Risk', 
      color: theme.palette.warning.main, 
      bg: alpha(theme.palette.warning.main, 0.1),
      severity: 'warning' as const
    };
    return { 
      level: 'High Risk', 
      color: theme.palette.error.main, 
      bg: alpha(theme.palette.error.main, 0.1),
      severity: 'error' as const
    };
  };

  const getScoreChangeIcon = (scoreDiff: number) => {
    if (scoreDiff > 0) return <TrendingUpIcon fontSize="small" color="error" />;
    if (scoreDiff < 0) return <TrendingDownIcon fontSize="small" color="success" />;
    return <TrendingFlatIcon fontSize="small" color="action" />;
  };

  if (existingResults.length === 0) {
    return null;
  }

  // FIXED: Process temporal sequence properly
  const processTemporalSequence = () => {
    console.log('Processing temporal sequence with existingResults:', existingResults);

    // Get the current annotation details
    const currentAnnotation = existingResults[0]?.annotationDetails;
    if (!currentAnnotation) {
      console.log('No current annotation found');
      return { 
        temporalSequence: [], 
        previousForComparison: null, 
        relatedImages: [], 
        currentIndex: -1 
      };
    }

    console.log('Current annotation:', currentAnnotation);

    // Get all historical assessments from ALL results
    const allHistoricalAssessments = existingResults.reduce((acc, result) => {
      console.log('Processing result:', result);
      
      if (result.historicalAssessments && Array.isArray(result.historicalAssessments)) {
        console.log('Found historical assessments:', result.historicalAssessments);
        return [...acc, ...result.historicalAssessments];
      }
      return acc;
    }, [] as any[]);

    console.log('All historical assessments found:', allHistoricalAssessments);

    // Find assessments for the same mole location (same coordinates within tolerance)
    const coordinateTolerance = 0.05;
    const sameLocationAssessments = allHistoricalAssessments.filter(historical => {
      if (!historical.annotationDetails) {
        console.log('Historical assessment missing annotationDetails:', historical);
        return false;
      }
      
      const coordMatch = (
        Math.abs(historical.annotationDetails.x - currentAnnotation.x) < coordinateTolerance &&
        Math.abs(historical.annotationDetails.y - currentAnnotation.y) < coordinateTolerance &&
        Math.abs(historical.annotationDetails.width - currentAnnotation.width) < coordinateTolerance &&
        Math.abs(historical.annotationDetails.height - currentAnnotation.height) < coordinateTolerance
      );
      
      console.log('Coordinate match for historical:', {
        historical: historical.annotationDetails,
        current: currentAnnotation,
        coordMatch
      });
      
      return coordMatch;
    });

    console.log('Same location assessments:', sameLocationAssessments);

    // Process current assessments
    const currentAssessments = existingResults.map((assessment, index) => ({
      id: assessment._additional?.id || `current_${index}`,
      annotationId: assessment.annotationDetails?.annotationId || assessment._additional?.id,
      assessment: assessment,
      imageUrl: assessment.annotationDetails?.linkedMicroUrl || assessment.annotationDetails?.macroImageUrl,
      createdAt: assessment.createdAt || new Date().toISOString(),
      isCurrent: true,
      sessionType: 'current',
      annotationDetails: assessment.annotationDetails
    }));

    // Process historical assessments
    const historicalAssessments = sameLocationAssessments.map((historical, index) => ({
      id: historical.annotationId || historical._additional?.id || `historical_${index}`,
      annotationId: historical.annotationId || historical._additional?.id,
      assessment: historical.assessment || historical,
      imageUrl: historical.imageUrl || historical.annotationDetails?.linkedMicroUrl || historical.annotationDetails?.macroImageUrl,
      createdAt: historical.createdAt || historical.assessment?.createdAt || new Date().toISOString(),
      isCurrent: false,
      sessionType: historical.sessionType || 'baseline',
      annotationDetails: historical.annotationDetails
    }));

    // Combine and sort all assessments chronologically
    const allAssessments = [
      ...currentAssessments,
      ...historicalAssessments
    ].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    console.log('All assessments sorted:', allAssessments);

    // Create temporal sequence with proper previous/next relationships
    const temporalSequence: TemporalAssessment[] = allAssessments.map((assessment, index) => ({
      ...assessment,
      temporalIndex: index,
      isBaseline: index === 0,
      previousAssessment: index > 0 ? allAssessments[index - 1] as TemporalAssessment : null,
      nextAssessment: index < allAssessments.length - 1 ? allAssessments[index + 1] as TemporalAssessment : null,
      isLatest: index === allAssessments.length - 1
    }));

    console.log('Temporal sequence:', temporalSequence);

    // Get current assessment in sequence
    const currentIndex = temporalSequence.findIndex(seq => 
      seq.isCurrent && (seq.id === currentAssessmentId || seq.annotationId === currentAssessmentId)
    );

    console.log('Current index in sequence:', currentIndex);

    // Find the immediate previous assessment for comparison
    let previousForComparison = null;
    if (currentIndex > 0) {
      previousForComparison = temporalSequence[currentIndex - 1];
    } else if (temporalSequence.length > 1) {
      // If current is not found but we have a sequence, get the most recent non-current
      const nonCurrentAssessments = temporalSequence.filter(seq => !seq.isCurrent);
      previousForComparison = nonCurrentAssessments[nonCurrentAssessments.length - 1] || null;
    }

    console.log('Previous for comparison:', previousForComparison);

    // Get unrelated images (images not part of the temporal sequence)
    const allRelatedImages = existingResults.reduce((acc, result) => {
      if (result.relatedImages) {
        return [...acc, ...result.relatedImages.filter(img => 
          !temporalSequence.some(seq => seq.annotationId === img.annotationId)
        )];
      }
      return acc;
    }, [] as any[]);

    // Remove duplicates
    const uniqueRelatedImages = allRelatedImages.filter((image, index, self) =>
      index === self.findIndex(i => i.annotationId === image.annotationId)
    );

    const result = { 
      temporalSequence, 
      previousForComparison,
      relatedImages: uniqueRelatedImages,
      currentIndex
    };

    console.log('Final processed result:', result);
    return result;
  };

  const { temporalSequence, previousForComparison, relatedImages, currentIndex } = processTemporalSequence();

  return (
    <Accordion defaultExpanded={!isUpdating}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" />
          Temporal Assessment History ({temporalSequence.length > 0 ? temporalSequence.length : existingResults.length})
          {previousForComparison && (
            <Chip 
              label={`Previous: ${previousForComparison.isBaseline ? 'Baseline' : 'Follow-up'}`}
              size="small" 
              color="primary" 
              variant="outlined"
            />
          )}
          {temporalSequence.length > 1 && (
            <Chip 
              label={`${temporalSequence.length} Time Points`} 
              size="small" 
              color="secondary" 
              variant="outlined"
            />
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          
          {/* Debug Information */}
          <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Debug Information:</Typography>
            <Typography variant="caption" display="block">
              Existing Results: {existingResults.length}
            </Typography>
            <Typography variant="caption" display="block">
              Temporal Sequence: {temporalSequence.length}
            </Typography>
            <Typography variant="caption" display="block">
              Previous for Comparison: {previousForComparison ? 'Yes' : 'No'}
            </Typography>
            <Typography variant="caption" display="block">
              Current Index: {currentIndex}
            </Typography>
            <Typography variant="caption" display="block">
              Historical Assessments Found: {existingResults.reduce((count, result) => 
                count + (result.historicalAssessments?.length || 0), 0)}
            </Typography>
          </Paper>

          {/* Show Previous Assessment for Comparison */}
          {previousForComparison && (
            <Paper elevation={2} sx={{ 
              p: 3, 
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: 2
            }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompareIcon color="primary" />
                Previous Assessment for Comparison
                <Chip 
                  label={previousForComparison.isBaseline ? "Baseline" : `Follow-up ${previousForComparison.temporalIndex}`} 
                  color="primary" 
                  size="small" 
                  variant="filled"
                />
                <Chip 
                  label={new Date(previousForComparison.createdAt).toLocaleDateString()} 
                  color="default" 
                  size="small" 
                  variant="outlined"
                />
              </Typography>
              
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                    <Chip label={`A: ${previousForComparison.assessment?.asymmetry || previousForComparison.assessment.asymmetry}`} size="small" />
                    <Chip label={`B: ${previousForComparison.assessment?.borders || previousForComparison.assessment.borders}`} size="small" />
                    <Chip label={`C: ${previousForComparison.assessment?.color || previousForComparison.assessment.color}`} size="small" />
                    <Chip label={`D: ${previousForComparison.assessment?.diameter || previousForComparison.assessment.diameter}`} size="small" />
                    <Chip label={`E: ${previousForComparison.assessment?.evolution || previousForComparison.assessment.evolution}`} size="small" />
                    <Chip 
                      label={`Total: ${previousForComparison.assessment?.totalScore || previousForComparison.assessment.totalScore}`} 
                      color={getRiskLevel(previousForComparison.assessment?.totalScore || previousForComparison.assessment.totalScore).severity}
                      variant="filled"
                    />
                  </Box>
                  
                  <Typography variant="body2" sx={{ fontWeight: 600, color: getRiskLevel(previousForComparison.assessment?.totalScore || previousForComparison.assessment.totalScore).color }}>
                    {getRiskLevel(previousForComparison.assessment?.totalScore || previousForComparison.assessment.totalScore).level}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  {previousForComparison.imageUrl && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Paper elevation={2} sx={{ p: 1, borderRadius: 2, display: 'inline-block' }}>
                        <img
                          src={`${process.env.REACT_APP_API_URL}${previousForComparison.imageUrl}`}
                          alt="Previous assessment"
                          style={{ 
                            width: 120, 
                            height: 120, 
                            objectFit: 'cover', 
                            borderRadius: 8 
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                          {previousForComparison.isBaseline ? 'Baseline' : 'Previous Follow-up'}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'right' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<CompareIcon />}
                      onClick={() => {
                        if (existingResults[0]) {
                          onCompareAssessments(existingResults[0], previousForComparison);
                        }
                      }}
                      sx={{ mb: 1 }}
                    >
                      Compare with Current
                    </Button>
                    
                    {existingResults[0] && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                        {getScoreChangeIcon(existingResults[0].totalScore - (previousForComparison.assessment?.totalScore || previousForComparison.assessment.totalScore))}
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Score Change: {existingResults[0].totalScore - (previousForComparison.assessment?.totalScore || previousForComparison.assessment.totalScore) > 0 ? '+' : ''}
                          {existingResults[0].totalScore - (previousForComparison.assessment?.totalScore || previousForComparison.assessment.totalScore)}
                        </Typography>
                      </Box>
                    )}
                    
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {previousForComparison.assessment?.notes || previousForComparison.assessment.notes || 'No notes'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Message if no previous assessment found */}
          {!previousForComparison && existingResults.length > 0 && (
            <Paper elevation={1} sx={{ 
              p: 2, 
              bgcolor: alpha(theme.palette.warning.main, 0.05),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              borderRadius: 1
            }}>
              <Typography variant="body2" color="text.secondary">
                <strong>No Previous Assessment Found:</strong> This appears to be the first assessment for this mole location, 
                or the historical assessments are not properly linked. Check the backend data structure.
              </Typography>
            </Paper>
          )}

          {/* Full Temporal Sequence */}
          {temporalSequence.length > 1 && (
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimelineIcon color="secondary" />
                Complete Temporal Sequence ({temporalSequence.length} assessments)
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Chronological progression of ABCDE assessments for this mole location.
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {temporalSequence.map((assessment, index) => (
                  <Paper 
                    key={`${assessment.id}_${index}`}
                    elevation={assessment.isCurrent ? 3 : 1} 
                    sx={{ 
                      p: 2,
                      border: assessment.isCurrent ? `2px solid ${theme.palette.primary.main}` : 
                              assessment.isBaseline ? `1px solid ${theme.palette.success.main}` : 
                              `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
                      bgcolor: assessment.isCurrent ? alpha(theme.palette.primary.main, 0.05) :
                               assessment.isBaseline ? alpha(theme.palette.success.main, 0.05) : 'transparent'
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={2}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={assessment.isBaseline ? "Baseline" : 
                                   assessment.isCurrent ? "Current" : 
                                   `Follow-up ${assessment.temporalIndex}`} 
                            color={assessment.isBaseline ? "success" : 
                                   assessment.isCurrent ? "primary" : "default"}
                            variant="filled"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(assessment.createdAt).toLocaleDateString()}
                          </Typography>
                          {assessment.isLatest && !assessment.isCurrent && (
                            <Chip label="Latest" color="info" size="small" variant="outlined" />
                          )}
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Chip label={`A: ${assessment.assessment?.asymmetry || assessment.assessment.asymmetry}`} size="small" />
                          <Chip label={`B: ${assessment.assessment?.borders || assessment.assessment.borders}`} size="small" />
                          <Chip label={`C: ${assessment.assessment?.color || assessment.assessment.color}`} size="small" />
                          <Chip label={`D: ${assessment.assessment?.diameter || assessment.assessment.diameter}`} size="small" />
                          <Chip label={`E: ${assessment.assessment?.evolution || assessment.assessment.evolution}`} size="small" />
                          <Chip 
                            label={`Total: ${assessment.assessment?.totalScore || assessment.assessment.totalScore}`} 
                            color={getRiskLevel(assessment.assessment?.totalScore || assessment.assessment.totalScore).severity}
                            variant="filled"
                          />
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={3}>
                        {assessment.imageUrl && (
                          <Box sx={{ textAlign: 'center' }}>
                            <img
                              src={`${process.env.REACT_APP_API_URL}${assessment.imageUrl}`}
                              alt={`Assessment ${index}`}
                              style={{ 
                                width: 80, 
                                height: 80, 
                                objectFit: 'cover', 
                                borderRadius: 8 
                              }}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </Box>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} md={3}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {!assessment.isCurrent && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => onNavigateToAssessment(assessment.annotationId)}
                            >
                              View Assessment
                            </Button>
                          )}
                          
                          {assessment.previousAssessment && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getScoreChangeIcon((assessment.assessment?.totalScore || assessment.assessment.totalScore) - 
                                                 (assessment.previousAssessment.assessment?.totalScore || assessment.previousAssessment.assessment.totalScore))}
                              <Typography variant="caption" color="text.secondary">
                                Score change: {((assessment.assessment?.totalScore || assessment.assessment.totalScore) - 
                                               (assessment.previousAssessment.assessment?.totalScore || assessment.previousAssessment.assessment.totalScore)) > 0 ? '+' : ''}
                                {(assessment.assessment?.totalScore || assessment.assessment.totalScore) - 
                                 (assessment.previousAssessment.assessment?.totalScore || assessment.previousAssessment.assessment.totalScore)}
                              </Typography>
                            </Box>
                          )}
                          
                          <Typography variant="body2" sx={{ fontWeight: 600, color: getRiskLevel(assessment.assessment?.totalScore || assessment.assessment.totalScore).color }}>
                            {getRiskLevel(assessment.assessment?.totalScore || assessment.assessment.totalScore).level}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            </Paper>
          )}

          {/* Fallback: Show Current Results if No Temporal Sequence */}
          {temporalSequence.length <= 1 && existingResults.map((result) => (
            <Paper 
              key={result._additional.id} 
              elevation={1} 
              sx={{ 
                p: 3,
                border: currentAssessmentId === result._additional.id ? `2px solid ${theme.palette.primary.main}` : 'none'
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip label={`A: ${result.asymmetry}`} size="small" />
                    <Chip label={`B: ${result.borders}`} size="small" />
                    <Chip label={`C: ${result.color}`} size="small" />
                    <Chip label={`D: ${result.diameter}`} size="small" />
                    <Chip label={`E: ${result.evolution}`} size="small" />
                    <Chip 
                      label={`Total: ${result.totalScore}`} 
                      color={getRiskLevel(result.totalScore).severity}
                      variant="filled"
                    />
                    {currentAssessmentId === result._additional.id && (
                      <Chip 
                        label="Current" 
                        color="primary" 
                        variant="filled" 
                        size="small" 
                      />
                    )}
                  </Box>
                  
                  {(result.createdAt || result.updatedAt) && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {result.createdAt && `Created: ${new Date(result.createdAt).toLocaleDateString()}`}
                        {result.updatedAt && result.updatedAt !== result.createdAt && 
                          ` | Updated: ${new Date(result.updatedAt).toLocaleDateString()}`
                        }
                      </Typography>
                    </Box>
                  )}
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {getRiskLevel(result.totalScore).level}
                    </Typography>
                    {currentAssessmentId !== result._additional.id && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onEditAssessment(result)}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>
                </Grid>
                
                {result.annotationDetails && (result.annotationDetails.linkedMicroUrl || result.annotationDetails.macroImageUrl) && (
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageIcon fontSize="small" color="secondary" />
                        Assessment Image
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Paper elevation={1} sx={{ p: 1, borderRadius: 2 }}>
                          <img
                            src={`${process.env.REACT_APP_API_URL}${result.annotationDetails.linkedMicroUrl || result.annotationDetails.macroImageUrl}`}
                            alt="Current assessment"
                            style={{ 
                              width: 100, 
                              height: 100, 
                              objectFit: 'cover', 
                              borderRadius: 8 
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </Paper>
                        
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            <strong>Label:</strong> {result.annotationDetails.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            <strong>Position:</strong> {Math.round(result.annotationDetails.x * 100)}%, {Math.round(result.annotationDetails.y * 100)}%
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {result.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Notes:</strong> {result.notes}
                    </Typography>
                  </Grid>
                )}
                {result.recommendation && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Recommendation:</strong> {result.recommendation}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          ))}

          {/* Unrelated Images */}
          {relatedImages.length > 0 && (
            <Paper elevation={1} sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.03) }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhotoLibraryIcon color="info" />
                Other Images in Area ({relatedImages.length})
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {relatedImages.map((image, imgIndex) => (
                  <Box key={`unrelated_${imgIndex}`} sx={{ textAlign: 'center' }}>
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        p: 1, 
                        borderRadius: 2,
                        cursor: 'pointer',
                        '&:hover': { transform: 'scale(1.05)' }
                      }}
                      onClick={() => onNavigateToAssessment(image.annotationId)}
                    >
                      <img
                        src={`${process.env.REACT_APP_API_URL}${image.linkedMicroUrl || image.macroImageUrl}`}
                        alt={image.label}
                        style={{ 
                          width: 80, 
                          height: 80, 
                          objectFit: 'cover', 
                          borderRadius: 4 
                        }}
                      />
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, maxWidth: 80 }}>
                        {image.label}
                      </Typography>
                    </Paper>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default AssessmentHistory;